import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";

interface MyDoc {
  _id: string;
  filename: string;
  description: string | null;
  uploadedAt: number;
  url: string | null;
}

// Tenant-personal document drop — receipts, insurance certs, ID
// copies, photos. Scoped to the tenant's active tenancy; previous
// tenancies' docs aren't visible here. Landlord has separate
// read+delete access (added on /tenants in a future pass).
export function MyDocumentsSection() {
  const docs = useQuery(anyApi.documents.myDocs, {}) as MyDoc[] | undefined;
  const generateUploadUrl = useMutation(anyApi.documents.generateUploadUrl);
  const attach = useMutation(anyApi.documents.attach);
  const remove = useMutation(anyApi.documents.remove);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const uploadUrl = (await generateUploadUrl({})) as string;
        const res = await fetch(uploadUrl, {
          method: "POST",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`upload failed: ${res.status} ${res.statusText}`);
        }
        const { storageId } = (await res.json()) as { storageId: string };
        await attach({ storageId, filename: file.name });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (docId: string, filename: string) => {
    if (!confirm(`Delete "${filename}"? This can't be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await remove({ docId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Documents</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Receipts, insurance certs, ID copies, photos. Scoped to your
            current tenancy.
          </p>
        </div>
        <label className="cursor-pointer shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="sr-only"
            disabled={busy}
          />
          <span className="inline-block rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors">
            {busy ? "Uploading…" : "Upload"}
          </span>
        </label>
      </header>
      <Body docs={docs} error={error} onRemove={handleRemove} busy={busy} />
    </section>
  );
}

function Body({
  docs,
  error,
  onRemove,
  busy,
}: {
  docs: MyDoc[] | undefined;
  error: string | null;
  onRemove: (id: string, filename: string) => void;
  busy: boolean;
}) {
  if (docs === undefined) {
    return <p className="px-6 py-4 text-sm text-slate-500">Loading…</p>;
  }
  if (docs.length === 0 && !error) {
    return (
      <div className="px-6 py-8 text-center">
        <p className="text-sm font-medium text-slate-900">
          No documents yet.
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Upload a PDF or image — receipts, insurance, anything you want
          on file.
        </p>
      </div>
    );
  }
  return (
    <div className="px-6 py-4">
      {error && <p className="mb-3 text-xs text-red-600">{error}</p>}
      <ul className="divide-y divide-slate-100">
        {docs.map((d) => (
          <li
            key={d._id}
            className="py-3 flex items-center justify-between gap-3 first:pt-0"
          >
            <div className="min-w-0 flex-1">
              {d.url ? (
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-slate-900 hover:underline truncate block"
                >
                  {d.filename}
                </a>
              ) : (
                <span className="text-sm font-medium text-slate-500 truncate block">
                  {d.filename}
                </span>
              )}
              <p className="text-xs text-slate-500">
                Uploaded {formatTimestamp(d.uploadedAt)}
                {d.description ? ` · ${d.description}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(d._id, d.filename)}
              disabled={busy}
              className="shrink-0 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
